// ParamedicalCheckout.js
// DEPRECATED: This component is kept for backward compatibility
// Please use ParamedicalBooking.js instead which has integrated payment flow

import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../../api/axios';
import { AuthContext } from '../../../context/AuthContext';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import PaymentGateway from '../../payment/PaymentGateway';

const ParamedicalCheckout = () => {
  const { id } = useParams(); // Service ID from URL
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useContext(AuthContext);
  const [serviceData, setServiceData] = useState(null);
  const [bookingData, setBookingData] = useState(location.state?.bookingData || null);
  const [loading, setLoading] = useState(true);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  useEffect(() => {
    if (!token) {
      alert('Please login to continue');
      navigate('/login');
      return;
    }

    // Check if booking data exists
    if (!bookingData) {
      alert('No booking data found. Please start from booking page.');
      navigate(`/paramedical/book/${id}`);
      return;
    }

    // Fetch paramedical service details
    api.get(`/paramedical-services/${id}/`)
      .then(res => {
        if (res.data.success) {
          setServiceData(res.data.data);
        } else {
          setPaymentError('Failed to load service details');
        }
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setPaymentError('Failed to load service details');
        setLoading(false);
      });
  }, [id, token, bookingData, navigate]);

  const handlePaymentSuccess = async (razorpayResponse) => {
    setLoading(true);
    setPaymentError('');

    try {
      // Payment is already verified by PaymentGateway component
      // Now create the paramedical booking with complete data
      const payload = {
        service_id: parseInt(id),
        appointment_date: bookingData.appointment_date,
        appointment_time: bookingData.appointment_time,
        address: bookingData.address,
        latitude: parseFloat(bookingData.latitude),
        longitude: parseFloat(bookingData.longitude),
        special_instructions: bookingData.special_instructions || '',
        emergency_contact_name: bookingData.emergency_contact_name || '',
        emergency_contact_phone: bookingData.emergency_contact_phone || '',
      };

      const response = await api.post('/paramedical-services/book/', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        const bookingId = response.data.data.id;
        navigate(`/order-success?order_id=PARA-${bookingId}&total=${servicePrice}&payment_id=${razorpayResponse.payment_id}&service_type=paramedical`);
      } else {
        throw new Error(response.data.message || 'Booking failed');
      }
    } catch (error) {
      console.error('Booking failed:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to book appointment after payment. Please contact support.';
      setPaymentError(errorMessage);
      alert(errorMessage + '\nYour payment was successful. Please contact support with your payment ID: ' + razorpayResponse.payment_id);
      setLoading(false);
    }
  };
  
  const handlePaymentError = (error) => {
    console.error('Payment failed:', error);
    setPaymentError('Payment failed. Please try again.');
    setLoading(false);
  };
  
  const initiatePayment = () => {
    if (!token) {
      alert('Please login to complete payment');
      navigate('/login');
      return;
    }
    setShowPayment(true);
    setPaymentError('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 text-lg">Loading checkout details...</p>
        </div>
      </div>
    );
  }

  if (!serviceData && !loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center bg-white rounded-lg shadow-lg p-12 max-w-md mx-auto">
          <div className="text-gray-400 text-6xl mb-4">❌</div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Service Not Found</h3>
          <p className="text-gray-500">Unable to load service information. Please try again.</p>
        </div>
      </div>
    );
  }

  const servicePrice = serviceData?.offer_price || serviceData?.price || 0;

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
            {/* Deprecation Notice */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    <strong>Note:</strong> For best experience, please use the integrated booking flow from the service detail page.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">Paramedical Service Payment</h2>

            {/* Booking Summary */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Booking Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">Service</label>
                    <p className="text-lg font-semibold text-gray-900 mt-1">{serviceData.title}</p>
                  </div>
                  {bookingData && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">Appointment Date & Time</label>
                        <p className="text-gray-900 mt-1">
                          {bookingData.appointment_date} at {bookingData.appointment_time}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">Address</label>
                        <p className="text-gray-700 mt-1">{bookingData.address}</p>
                      </div>
                    </>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">Total Amount</label>
                  <p className="text-3xl font-bold mt-1" style={{ color: '#104a5f' }}>₹{servicePrice}</p>
                  {bookingData?.priceBreakdown && (
                    <div className="mt-3 text-sm text-gray-600 space-y-1">
                      <p>Service: ₹{bookingData.priceBreakdown.service_price}</p>
                      <p>Travel ({bookingData.priceBreakdown.distance_km} km): ₹{bookingData.priceBreakdown.travel_cost}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Error Message */}
            {paymentError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
                {paymentError}
              </div>
            )}

            {/* Payment Section */}
            {!showPayment ? (
              <div className="text-center">
                <p className="text-gray-600 mb-6">Click below to proceed with secure payment</p>
                <button
                  onClick={initiatePayment}
                  className="px-8 py-3 text-white font-semibold rounded-lg transition-all duration-200 hover:opacity-90 hover:scale-105"
                  style={{ backgroundColor: '#104a5f' }}
                >
                  Pay ₹{servicePrice} & Book Service
                </button>
              </div>
            ) : (
              <PaymentGateway
                amount={servicePrice}
                serviceType="paramedical_service"
                serviceId={serviceData.id}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
                customerDetails={{
                  name: user?.name || '',
                  email: user?.email || '',
                  mobile: user?.mobile || ''
                }}
              />
            )}

            {loading && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderBottomColor: '#104a5f' }}></div>
                  <p className="text-gray-600">Processing your booking...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ParamedicalCheckout;